import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Upload, X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import * as accountService from '../../../services/accountService';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Photo Gallery Component
 * Before/after comparison with date-stamped photos
 */
export default function PhotoGallery({ planId }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    if (planId && user) {
      loadPhotos();
    }
  }, [planId, user]);

  const loadPhotos = async () => {
    setIsLoading(true);
    try {
      const result = await accountService.getProgressEntries(user.id, {
        entryType: 'photo',
      });
      if (result.success) {
        // Filter photos for this plan
        const planPhotos = (result.data || []).filter(
          (e) => e.program_id === planId
        );
        setPhotos(planPhotos);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !user) return;

    // TODO: Implement photo upload to Supabase Storage
    // For now, create a progress entry with photo metadata
    try {
      const result = await accountService.addProgressEntry(user.id, {
        entryType: 'photo',
        entryDate: new Date().toISOString().split('T')[0],
        data: {
          photo_url: URL.createObjectURL(uploadFile), // Temporary - should upload to storage
          filename: uploadFile.name,
        },
        notes: '',
      });

      if (result.success) {
        setShowUpload(false);
        setUploadFile(null);
        loadPhotos();
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading photos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Progress Photos</h2>
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Upload Photo
        </button>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upload Progress Photo</h3>
              <button
                onClick={() => {
                  setShowUpload(false);
                  setUploadFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {uploadFile && (
                <div className="mt-4">
                  <img
                    src={URL.createObjectURL(uploadFile)}
                    alt="Preview"
                    className="w-full rounded-lg"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={!uploadFile}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  Upload
                </button>
                <button
                  onClick={() => {
                    setShowUpload(false);
                    setUploadFile(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, index) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => setSelectedPhoto(photo)}
            >
              <div className="aspect-square bg-gray-100 relative">
                {photo.data?.photo_url ? (
                  <img
                    src={photo.data.photo_url}
                    alt={`Progress photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Camera className="w-16 h-16 text-gray-300" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(photo.entry_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No photos yet</h3>
          <p className="text-gray-600 mb-4">Start tracking your progress with photos</p>
          <button
            onClick={() => setShowUpload(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Upload Your First Photo
          </button>
        </div>
      )}

      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            {selectedPhoto.data?.photo_url && (
              <img
                src={selectedPhoto.data.photo_url}
                alt="Progress photo"
                className="w-full rounded-lg"
              />
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-4 rounded-b-lg">
              <p className="font-medium">
                {new Date(selectedPhoto.entry_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              {selectedPhoto.notes && (
                <p className="text-sm mt-1">{selectedPhoto.notes}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
